import { apiClient } from './client';

export type BankAccountType = 'CURRENT' | 'SAVINGS' | 'OVERDRAFT' | 'CASH_CREDIT';

export interface BankAccount {
  id: string;
  companyId: string;
  ledgerAccountId: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
  ifscCode?: string | null;
  branchName?: string | null;
  accountType: BankAccountType;
  accountHolderName?: string | null;
  openingBalance: number;
  openingBalanceDate?: string | null;
  currentBookBalance: number;
  lastReconciledDate?: string | null;
  lastReconciledBalance?: number | null;
  isPrimary: boolean;
  isActive: boolean;
  ledgerCode?: string;
  ledgerName?: string;
}

export interface BankAccountInput {
  bankName: string;
  accountNumber: string;
  accountName?: string;
  ifscCode?: string;
  branchName?: string;
  accountType?: BankAccountType;
  accountHolderName?: string;
  openingBalance?: number;
  openingBalanceDate?: string | null;
  isPrimary?: boolean;
  ledgerAccountId?: string;
}

/** Cash in hand + every active bank account ledger — for "received in / paid from" pickers. */
export interface CashBankLedger {
  id: string;
  code: string;
  name: string;
  kind: 'CASH' | 'BANK';
  bankName?: string;
  accountNumber?: string;
  isPrimary?: boolean;
  currentBalance: number;
}

export type StatementMatchStatus =
  | 'UNMATCHED'
  | 'SUGGESTED'
  | 'AUTO_MATCHED'
  | 'MANUAL_MATCHED'
  | 'BANK_ERROR'
  | 'IGNORED';

export type AdjustmentType = 'BANK_CHARGES' | 'BANK_INTEREST' | 'DIRECT_RECEIPT' | 'DIRECT_PAYMENT' | 'OTHER';

export interface BankStatementEntry {
  id: string;
  date: string;
  valueDate?: string | null;
  description: string;
  referenceNo?: string | null;
  debit: number;
  credit: number;
  balance?: number | null;
  matchStatus: StatementMatchStatus;
  matchedEntryId?: string | null;
  matchedJvNumber?: string | null;
  matchedJvDate?: string | null;
  matchedJvType?: string | null;
  suggestedEntryId?: string | null;
  suggestedJvNumber?: string | null;
  suggestedDate?: string | null;
  suggestedNarration?: string | null;
  suggestedParty?: string | null;
  matchScore?: number | null;
  adjustmentType?: AdjustmentType | null;
  adjustmentPartyName?: string | null;
  adjustmentAccount?: string | null;
  isCarriedForward: boolean;
  notes?: string | null;
}

export interface BookEntry {
  id: string;
  voucherId: string;
  voucherNumber: string;
  voucherType: string;
  referenceTable?: string | null;
  date: string;
  debit: number;
  credit: number;
  narration: string | null;
  partyName?: string | null;
  chequeNo?: string | null;
  referenceNo?: string | null;
  paymentMode?: string | null;
  isMatched: boolean;
  matchedStatementId?: string | null;
  suggestedStatementId?: string | null;
  matchStatus: string;
  isStale: boolean;
  isCarriedForward: boolean;
}

export interface ReconciliationHeader {
  id: string;
  bankAccountId: string;
  periodStart: string;
  periodEnd: string;
  statementClosingDate: string;
  openingBalanceBook: number;
  openingBalanceBank: number;
  closingBalanceBook: number;
  closingBalanceBank: number;
  totalMatched: number;
  totalSuggested: number;
  totalUnmatchedBank: number;
  totalUnmatchedBook: number;
  outstandingCheques: number;
  depositsInTransit: number;
  bankOnlyCredits: number;
  bankOnlyDebits: number;
  bankErrors: number;
  adjustedBankBalance: number;
  adjustedBookBalance: number;
  staleChequesCount: number;
  staleChequesAmount: number;
  difference: number;
  status: 'OPEN' | 'RECONCILED' | 'LOCKED';
  lockedAt?: string | null;
  lockedByEmail?: string | null;
  overrideReason?: string | null;
}

export interface ReconciliationStartInfo {
  startDate: string;
  entriesBeforeStart: number;
  bookBalanceAtStart: number;
  statementOpeningBalance: number;
  openingDifference: number;
}

export interface ReconciliationWorkspace {
  reconciliation: ReconciliationHeader;
  /** Present when the bank account has a statement start date (Settings → Bank Accounts). */
  startInfo: ReconciliationStartInfo | null;
  bankAccount: BankAccount;
  statementEntries: BankStatementEntry[];
  bookEntries: BookEntry[];
}

export interface ReconciliationHistoryItem extends ReconciliationHeader {
  accountName: string;
  accountNumber: string;
  bankName: string;
}

export interface StatementImportRow {
  date: string | number;
  description: string;
  debit: number;
  credit: number;
  balance?: number | null;
  referenceNo?: string;
}

const base = '/api/v1/finance/bank-recon';

export const bankReconApi = {
  // ── Bank accounts master (Settings → Bank Accounts) ──
  getBankAccounts: async (params?: { includeInactive?: boolean }) => {
    const res = await apiClient.get<{ success: boolean; data: BankAccount[] }>(`${base}/accounts`, { params });
    return res.data.data;
  },

  createBankAccount: async (data: BankAccountInput) => {
    const res = await apiClient.post<{ success: boolean; data: BankAccount; message: string }>(`${base}/accounts`, data);
    return res.data.data;
  },

  updateBankAccount: async (id: string, data: Partial<BankAccountInput> & { isActive?: boolean }) => {
    const res = await apiClient.put<{ success: boolean; data: BankAccount; message: string }>(`${base}/accounts/${id}`, data);
    return res.data.data;
  },

  deactivateBankAccount: async (id: string) => {
    const res = await apiClient.delete<{ success: boolean; data: BankAccount; message: string }>(`${base}/accounts/${id}`);
    return res.data.data;
  },

  getCashBankLedgers: async () => {
    const res = await apiClient.get<{ success: boolean; data: CashBankLedger[] }>(`${base}/cash-bank-ledgers`);
    return res.data.data;
  },

  // ── Reconciliation session ──
  startReconciliation: async (data: {
    bankAccountId: string;
    periodStart?: string;
    periodEnd?: string;
    statementClosingBalance?: number;
  }) => {
    const res = await apiClient.post<{ success: boolean; data: { id: string } }>(`${base}/reconciliation/start`, data);
    return res.data.data;
  },

  getWorkspace: async (reconciliationId: string) => {
    const res = await apiClient.get<{ success: boolean; data: ReconciliationWorkspace }>(`${base}/reconciliation/${reconciliationId}/workspace`);
    return res.data.data;
  },

  setStatementBalance: async (reconciliationId: string, closingBalance: number) => {
    const res = await apiClient.put<{ success: boolean; message: string }>(`${base}/reconciliation/${reconciliationId}/statement-balance`, { closingBalance });
    return res.data;
  },

  importStatement: async (reconciliationId: string, data: { bankAccountId: string; rows: StatementImportRow[] }) => {
    const res = await apiClient.post<{
      success: boolean;
      data: {
        totalRows: number;
        importedCount: number;
        skippedDuplicates: number;
        rejected: { row: number | null; reason: string }[];
      };
      message: string;
    }>(`${base}/reconciliation/${reconciliationId}/import-statement`, data);
    return res.data;
  },

  autoMatch: async (reconciliationId: string) => {
    const res = await apiClient.post<{
      success: boolean;
      data: { matchedCount: number; suggestedCount: number; reconciliation: ReconciliationHeader };
      message: string;
    }>(`${base}/reconciliation/${reconciliationId}/auto-match`);
    return res.data;
  },

  acceptSuggestion: async (reconciliationId: string, statementEntryId: string) => {
    const res = await apiClient.post<{ success: boolean; message: string }>(`${base}/reconciliation/${reconciliationId}/accept-suggestion`, { statementEntryId });
    return res.data;
  },

  rejectSuggestion: async (reconciliationId: string, statementEntryId: string) => {
    const res = await apiClient.post<{ success: boolean; message: string }>(`${base}/reconciliation/${reconciliationId}/reject-suggestion`, { statementEntryId });
    return res.data;
  },

  manualMatch: async (reconciliationId: string, data: { statementEntryId: string; bookEntryId: string }) => {
    const res = await apiClient.post<{ success: boolean; message: string }>(`${base}/reconciliation/${reconciliationId}/manual-match`, data);
    return res.data;
  },

  unmatch: async (reconciliationId: string, statementEntryId: string) => {
    const res = await apiClient.post<{ success: boolean; message: string }>(`${base}/reconciliation/${reconciliationId}/unmatch`, { statementEntryId });
    return res.data;
  },

  updateStatementStatus: async (statementEntryId: string, data: { status: 'UNMATCHED' | 'BANK_ERROR' | 'IGNORED'; notes?: string }) => {
    const res = await apiClient.put<{ success: boolean; message: string }>(`${base}/statements/${statementEntryId}/status`, data);
    return res.data;
  },

  deleteStatementEntry: async (statementEntryId: string) => {
    const res = await apiClient.delete<{ success: boolean; message: string }>(`${base}/statements/${statementEntryId}`);
    return res.data;
  },

  createAdjustment: async (
    statementEntryId: string,
    data: { adjustmentType: AdjustmentType; accountId?: string; partyId?: string; narration?: string }
  ) => {
    const res = await apiClient.post<{ success: boolean; data: { voucherNumber: string }; message: string }>(
      `${base}/statements/${statementEntryId}/create-jv`,
      data
    );
    return res.data;
  },

  lockReconciliation: async (reconciliationId: string, data?: { overrideReason?: string }) => {
    const res = await apiClient.post<{ success: boolean; data: ReconciliationHeader; message: string }>(`${base}/reconciliation/${reconciliationId}/lock`, data || {});
    return res.data;
  },

  getHistory: async (bankAccountId?: string) => {
    const res = await apiClient.get<{ success: boolean; data: ReconciliationHistoryItem[] }>(`${base}/history`, { params: { bankAccountId } });
    return res.data.data;
  },
};
